import React from "react";
import styles from "./Select.module.css";
import { getClassName } from "../../utils/className.js";
import SelectTooltip from "./SelectTooltip.jsx";

/**
 * @typedef {Object} SelectOption
 * @property {string | number} value - The unique value of the option.
 * @property {string} label - The display text of the option.
 * @property {boolean} [disabled] - Whether the option is disabled.
 */

/**
 * SelectTrigger renders the click/focus area of the custom single-select dropdown.
 * It renders an `<input>` element when searchable, otherwise a `<button>` element.
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.isSearchable - If true, enables search filtering in the input.
 * @param {boolean} props.isOpen - Whether the dropdown menu is open.
 * @param {string} props.searchValue - The current search text.
 * @param {SelectOption | null} props.selectedOption - The currently selected option.
 * @param {string} props.placeholder - Custom placeholder text.
 * @param {boolean} props.disabled - Whether interaction is disabled.
 * @param {string} props.triggerClassName - Calculated class names for the trigger element.
 * @param {React.CSSProperties} [props.triggerStyle] - Inline style applied to the trigger element (e.g. for a custom height).
 * @param {React.RefObject<HTMLInputElement | HTMLButtonElement>} props.triggerRef - Reference to the trigger element.
 * @param {(e: React.ChangeEvent<HTMLInputElement>) => void} props.handleInputChange - Event handler for search input changes.
 * @param {(e: React.MouseEvent) => void} props.handleInputClick - Event handler for input clicks.
 * @param {() => void} props.handleInputFocus - Event handler for input focus.
 * @param {(e: React.KeyboardEvent) => void} props.handleKeyDown - Event handler for key presses.
 * @param {() => void} props.handleToggle - Event handler for button clicks to toggle open state.
 * @param {(e: React.MouseEvent) => void} props.handleArrowClick - Event handler for clicking the arrow icon.
 * @param {(e: React.MouseEvent) => void} props.handleClearSearch - Event handler for clearing the search text.
 * @param {React.ComponentType | React.ReactElement} [props.arrowIcon] - Custom icon component or element to replace the default dropdown arrow.
 * @param {string} [props.tooltip] - Tooltip text shown (with a short hover delay) when hovering over the trigger.
 * @param {boolean} [props.tooltipLight] - If true, (white background, dark text).
 */
const SelectTrigger = ({
  isSearchable,
  isOpen,
  searchValue,
  selectedOption,
  placeholder,
  disabled,
  triggerClassName,
  triggerStyle,
  triggerRef,
  handleInputChange,
  handleInputClick,
  handleInputFocus,
  handleKeyDown,
  handleToggle,
  handleArrowClick,
  handleClearSearch,
  arrowIcon,
  tooltip,
  tooltipLight,
}) => {
  return (
    <>
      {isSearchable ? (
        <input
          ref={triggerRef}
          type="text"
          className={triggerClassName}
          style={triggerStyle}
          disabled={disabled}
          value={isOpen ? searchValue : (selectedOption ? selectedOption.label : "")}
          placeholder={isOpen && selectedOption ? selectedOption.label : placeholder}
          onChange={handleInputChange}
          onClick={handleInputClick}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-disabled={disabled}
        />
      ) : (
        <button
          ref={triggerRef}
          type="button"
          className={triggerClassName}
          style={triggerStyle}
          disabled={disabled}
          onClick={handleToggle}
          onKeyDown={handleKeyDown}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-disabled={disabled}
        >
          <span
            className={getClassName(styles.selectionText, "select-selection-text", {
              [styles.placeholder]: !selectedOption,
              "select-placeholder": !selectedOption,
            })}
          >
            {selectedOption ? selectedOption.label : placeholder}
          </span>
        </button>
      )}

      {(() => {
        if (!arrowIcon) {
          return (
            <svg
              className={getClassName(styles.arrowIcon, "select-arrow-icon", {
                [styles.arrowOpen]: isOpen,
                "select-arrow-open": isOpen,
              })}
              viewBox="0 0 12 7"
              aria-hidden="true"
              onClick={handleArrowClick}
            >
              <path d="M1 0 L6 5 L11 0 L12 1 L6 7 L0 1 Z" />
            </svg>
          );
        }

        const isComponent =
          typeof arrowIcon === "function" ||
          (typeof arrowIcon === "object" &&
            arrowIcon &&
            arrowIcon.$$typeof &&
            !React.isValidElement(arrowIcon));

        const element = isComponent ? React.createElement(arrowIcon) : arrowIcon;

        if (React.isValidElement(element)) {
          return React.cloneElement(element, {
            className: getClassName(
              styles.arrowIcon,
              "select-arrow-icon",
              {
                [styles.arrowOpen]: isOpen,
                "select-arrow-open": isOpen,
              },
              element.props.className
            ),
            onClick: (e) => {
              element.props.onClick?.(e);
              handleArrowClick(e);
            },
          });
        }

        return (
          <div
            className={getClassName(styles.arrowIcon, "select-arrow-icon", {
              [styles.arrowOpen]: isOpen,
              "select-arrow-open": isOpen,
            })}
            onClick={handleArrowClick}
          >
            {element}
          </div>
        );
      })()}

      {isSearchable && isOpen && searchValue && (
        <button
          type="button"
          className={getClassName(styles.clearSearchBtn, "select-clear-search-btn")}
          onClick={handleClearSearch}
          title="Clear search"
        >
          &times;
        </button>
      )}

      <SelectTooltip text={tooltip} light={tooltipLight} anchorRef={triggerRef} />
    </>
  );
};

export default SelectTrigger;
